import { Injectable } from '@angular/core';
import { MessageService, StorageService, UtilsService } from '../services';
import { objectToCamel } from 'ts-case-convert';
import { initialPaginationState } from '../store/initialisation/pagination';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { SuggestionService } from '../services/suggestion/suggestion.service';
import { ISuggestion } from '../models/suggestion';
import { initialSuggestionState } from '../store/initialisation/suggestions';
import { CommunicationUserType, CommunicationType, SuggestionSortType } from '../models/enums';
import { IMessage } from '../models';
import { initialMessagesState } from '../store/initialisation/messages';
import {
  DEFAULT_SUGGESTION_SORT,
  IMessagesFilters,
  ISuggestionsFilters,
  deserializeMessagesFilters,
  emptyMessagesFilters,
  deserializeSuggestionsFilters,
  emptySuggestionsFilters,
  parseMessagesFiltersFromStorage,
  parseSuggestionsFiltersFromStorage,
} from '../filters';

@Injectable({
  providedIn: 'root',
})
export class CommunicationsResolver {
  // Fingerprint of the inputs of the last successful list fetch, keyed by
  // `type/userType` so suggestions and messages have independent caches.
  // Skips redundant fetches when navigating between /suggestions/users and
  // /suggestions/users/:id (sibling routes always re-run the resolver).
  private lastFetchFingerprint: Record<string, string> = {};
  // Fingerprint without page, per type/userType pair. Lets us tell a pure page
  // change from a filter change so the pagination only collapses to
  // "currentPage ···" when the totals are genuinely unknown.
  private lastFilterFingerprint: Record<string, string> = {};
  // The list state objects are shared between user types, so the skip must also know
  // which type/userType pair the data currently in view belongs to
  private lastLoadedFingerprintKey: string | null = null;

  constructor(
    private suggestionService: SuggestionService,
    private messageService: MessageService,
    public storageService: StorageService,
    public utilsService: UtilsService
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async resolve(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot) {
    const type = (route.data['type'] || route.routeConfig?.path?.split('/')[0]) as CommunicationType;
    const userType = (route.data['userType'] || route.routeConfig?.path?.split('/')[1]) as CommunicationUserType;
    const fingerprintKey = `${type}/${userType}`;
    if (type === CommunicationType.SUGGESTIONS) this.storageService.setSuggestionsLastListType(userType);
    const fingerprint = await this.computeFingerprint(route, type, userType);
    const hasData = type === CommunicationType.SUGGESTIONS
      ? !!initialSuggestionState.value?.length
      : !!initialMessagesState.value?.length;
    if (fingerprint === this.lastFetchFingerprint[fingerprintKey] && hasData && this.lastLoadedFingerprintKey === fingerprintKey) {
      // Same inputs as the last successful fetch and the list is already in
      // view. The periodic interval (communications.component) handles staleness.
      this.storageService.setRouteName(fingerprintKey);
      return;
    }
    this.getCommunications(route);
  }

  // Cache key for the list fetch. Recomputed by both resolve() (to decide skip)
  // and getCommunications() (to store after a successful fetch). Same recipe in
  // both places so they always agree.
  private async computeFingerprint(
    route: ActivatedRouteSnapshot,
    type: CommunicationType,
    userType: CommunicationUserType,
    searchValueOverride?: string,
    pageOverride?: number,
  ): Promise<string> {
    const pageParam = route.queryParamMap.get('page');
    const pageFromUrl = pageParam ? Number(pageParam) : null;
    const page = pageOverride ?? (route.data[type]?.pagination.actualPage
      || (pageFromUrl && !isNaN(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1));
    const size = route.data[type]?.pagination.size || 10;
    const search = searchValueOverride ?? route.queryParamMap.get('search') ?? null;

    if (type === CommunicationType.SUGGESTIONS) {
      const sortParam = route.queryParamMap.get('sort') as SuggestionSortType | null;
      const suggestionSortType = sortParam
        ?? (await this.storageService.getSuggestionsSortType() ?? DEFAULT_SUGGESTION_SORT);
      const filters = await this.resolveSuggestionFilters(route);
      const servicesIds = filters.selectedServices.map(s => s.id!).filter(id => id != null);
      return JSON.stringify({
        userType, page, size, suggestionSortType, search,
        clients: [...filters.selectedClients].sort(),
        towns: [...filters.selectedTowns].sort(),
        services: [...servicesIds].sort(),
        types: [...filters.selectedTypes].sort(),
        status: [...filters.selectedStatus].sort(),
        priorities: [...filters.selectedPriorities].sort(),
        userResolutions: [...filters.selectedUserResolutions].sort(),
        visibilities: [...filters.selectedVisibilities].sort(),
        expeditions: filters.selectedExpeditions.map((expedition) => expedition.id).sort(),
      });
    }
    const filters = await this.resolveMessageFilters(route);
    return JSON.stringify({
      userType, page, size, search,
      clients: filters.selectedClients.map((c) => c.id).sort(),
      senders: filters.selectedSenders.map((s) => s.id).sort(),
      receivers: filters.selectedReceivers.map((r) => r.id).sort(),
      origin: filters.selectedOrigin,
      events: [...filters.selectedEvents].sort(),
      adminAlerts: filters.selectedAdminAlerts,
    });
  }

  async getCommunications(route: ActivatedRouteSnapshot, searchValueOverride?: string, silent: boolean = false) {
    const type = (route.data['type'] || route.routeConfig?.path?.split('/')[0]) as CommunicationType;
    const userType = (route.data['userType'] || route.routeConfig?.path?.split('/')[1]) as CommunicationUserType;
    this.storageService.setRouteName(`${type}/${userType}`);

    const pageParam = route.queryParamMap.get('page');
    const pageFromUrl = pageParam ? Number(pageParam) : null;
    const page = route.data[type]?.pagination.actualPage
      || (pageFromUrl && !isNaN(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1);
    const size = route.data[type]?.pagination.size || 10;
    const searchValue = searchValueOverride ?? (route.queryParamMap.get('search') ?? undefined);
    const fingerprintKey = `${type}/${userType}`;

    if (type === CommunicationType.SUGGESTIONS) {
      const sortParam = route.queryParamMap.get('sort') as SuggestionSortType | null;
      const suggestionSortType = sortParam
        ?? (await this.storageService.getSuggestionsSortType() ?? DEFAULT_SUGGESTION_SORT);
      const filters = await this.resolveSuggestionFilters(route);
      if (!silent) {
        initialSuggestionState.isFetchingSuggestion = true;
        initialSuggestionState.value = [];
      }
      const suggestionFilterFingerprint = await this.computeFingerprint(route, type, userType, searchValueOverride, 0);
      if (suggestionFilterFingerprint !== this.lastFilterFingerprint[fingerprintKey]) {
        initialSuggestionState.pagination.totalPages = 0;
        initialSuggestionState.pagination.totalElements = 0;
      }
      this.lastFilterFingerprint[fingerprintKey] = suggestionFilterFingerprint;
      const { content, numberOfElements, totalElements, totalPages, first, last } =
        await this.suggestionService.filterSuggestions(userType, page, size, suggestionSortType, filters, searchValue);
      const result = this.getSuggestionData(content, numberOfElements, totalElements, totalPages, first, last, page);
      this.lastFetchFingerprint[fingerprintKey] = await this.computeFingerprint(route, type, userType, searchValueOverride);
      this.lastLoadedFingerprintKey = fingerprintKey;
      return result;
    }

    const filters = await this.resolveMessageFilters(route);
    if (!silent) {
      initialMessagesState.isFetchingMessages = true;
      initialMessagesState.value = [];
    }
    const messagesFilterFingerprint = await this.computeFingerprint(route, type, userType, searchValueOverride, 0);
    if (messagesFilterFingerprint !== this.lastFilterFingerprint[fingerprintKey]) {
      initialMessagesState.pagination.totalPages = 0;
      initialMessagesState.pagination.totalElements = 0;
    }
    this.lastFilterFingerprint[fingerprintKey] = messagesFilterFingerprint;
    const { content, numberOfElements, totalElements, totalPages, first, last } =
      await this.messageService.filterMessages(userType, page, size, filters, searchValue);
    const result = this.getMessageData(content, numberOfElements, totalElements, totalPages, first, last, page);
    this.lastFetchFingerprint[fingerprintKey] = await this.computeFingerprint(route, type, userType, searchValueOverride);
    this.lastLoadedFingerprintKey = fingerprintKey;
    return result;
  }

  private async resolveSuggestionFilters(route: ActivatedRouteSnapshot): Promise<ISuggestionsFilters> {
    // URL is the source of truth whenever there is *any* query param. Storage is only
    // consulted on a truly bare URL (the "sidebar tab-switch" entry path).
    if (route.queryParamMap.keys.length > 0) {
      return deserializeSuggestionsFilters(route.queryParamMap);
    }
    return parseSuggestionsFiltersFromStorage(await this.storageService.getSuggestionsFilters()) ?? emptySuggestionsFilters();
  }

  private async resolveMessageFilters(route: ActivatedRouteSnapshot): Promise<IMessagesFilters> {
    if (route.queryParamMap.keys.length > 0) {
      return deserializeMessagesFilters(route.queryParamMap);
    }
    return parseMessagesFiltersFromStorage(await this.storageService.getMessagesFilters()) ?? emptyMessagesFilters();
  }

  getSuggestionData(
    content: any,
    number_of_elements: any,
    total_elements: any,
    total_pages: any,
    first: any,
    last: any,
    page: any,
  ) {
    const data = initialSuggestionState;
    data.value = objectToCamel(content) as Array<ISuggestion>;
    data.isFetchingSuggestion = false;
    data.pagination = initialPaginationState;
    data.pagination.numberOfElements = number_of_elements;
    data.pagination.totalPages = total_pages;
    data.pagination.totalElements = total_elements;
    data.pagination.actualPage = page;
    data.pagination.first = first;
    data.pagination.last = last;
    return data;
  };

  getMessageData(
    content: any,
    number_of_elements: any,
    total_elements: any,
    total_pages: any,
    first: any,
    last: any,
    page: any,
  ) {
    const data = initialMessagesState;
    data.value = objectToCamel(content) as Array<IMessage>;
    data.isFetchingMessages = false;
    data.pagination = initialPaginationState;
    data.pagination.numberOfElements = number_of_elements;
    data.pagination.totalPages = total_pages;
    data.pagination.totalElements = total_elements;
    data.pagination.actualPage = page;
    data.pagination.first = first;
    data.pagination.last = last;
    return data;
  };
}
